import { useCapitalGlobalProposalStep2Form } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step2-form.context';
import {
  getCapitalGlobalBankFieldLimits,
  getCapitalGlobalBankMaskConfig,
  validateCapitalGlobalBankField,
} from '@/features/proposals/components/capital-global/utils/capital-global-proposal-payment-bank-validation.util';
import {
  getPaymentTypeByCode,
  requiresBankAccountForPayment,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import type { CapitalGlobalStep1FieldApiLike } from '@/features/proposals/components/capital-global/utils/capital-global-step1-should-show-field-errors.util';
import type { CapitalGlobalProposalStepPagamentoValues } from '@/features/proposals/schemas/capital-global/proposal-step-pagamento.schema';
import type { PaymentType } from '@/services';

function isValidBillingDay(value: string): boolean {
  const n = Number(String(value || '').replace(/\D/g, ''));
  return Number.isInteger(n) && n >= 1 && n <= 31;
}

type BankFieldName = 'agency' | 'agencyDigit' | 'account' | 'accountDigit';

function renderBankField(
  fieldName: BankFieldName,
  values: CapitalGlobalProposalStepPagamentoValues,
  paymentTypes: PaymentType[],
  options: {
    form: ReturnType<typeof useCapitalGlobalProposalStep2Form>['form'];
    isViewMode: boolean;
    shouldShowStep2FieldErrors: (
      fieldApi: CapitalGlobalStep1FieldApiLike
    ) => boolean;
    isDisabled: boolean;
  }
) {
  const { form, isViewMode, shouldShowStep2FieldErrors, isDisabled } = options;
  const paymentType = getPaymentTypeByCode(values.paymentMethod, paymentTypes);
  const bankMaskConfig = getCapitalGlobalBankMaskConfig(
    values.paymentMethod,
    paymentTypes
  );
  const limits = getCapitalGlobalBankFieldLimits(
    fieldName,
    bankMaskConfig,
    paymentType?.accountValidationRules
  );

  const labels: Record<BankFieldName, string> = {
    agency: 'Agência',
    agencyDigit: 'Digito da Agência',
    account: 'Conta',
    accountDigit: 'Digito da Conta',
  };

  return (
    <form.AppField
      key={fieldName}
      name={fieldName}
      validators={{
        onChange: ({ value, fieldApi }) => {
          if (isViewMode) return undefined;
          if (!shouldShowStep2FieldErrors(fieldApi)) return undefined;
          const currentValues = getBankFieldValues(fieldApi.form.state.values);
          return validateCapitalGlobalBankField(
            fieldName,
            String(value || ''),
            currentValues,
            paymentTypes
          );
        },
      }}
      children={field => (
        <field.TextField
          label={labels[fieldName]}
          placeholder={limits.placeholder}
          required={limits.required}
          maxLength={limits.maxLength}
          disabled={isDisabled || !limits.enabled}
        />
      )}
    />
  );
}

function getBankFieldValues(
  formValues: CapitalGlobalProposalStepPagamentoValues
): CapitalGlobalProposalStepPagamentoValues {
  return formValues;
}

export function CapitalGlobalProposalPaymentFieldsSection() {
  const {
    form,
    isViewMode,
    shouldShowStep2FieldErrors,
    paymentMethodOptions,
    paymentTypes,
    isLoadingPaymentTypes,
    isPaymentTypesReady,
    isSubmitting,
  } = useCapitalGlobalProposalStep2Form();

  const isDisabled =
    isViewMode || isSubmitting || isLoadingPaymentTypes || !isPaymentTypesReady;

  const bankFieldOptions = {
    form,
    isViewMode,
    shouldShowStep2FieldErrors,
    isDisabled,
  };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 w-full'>
      <form.AppField
        name='paymentMethod'
        validators={{
          onChange: ({ value, fieldApi }) => {
            if (isViewMode) return undefined;
            if (!shouldShowStep2FieldErrors(fieldApi)) return undefined;
            if (!String(value || '').trim()) {
              return 'Selecione o meio de pagamento';
            }
            if (
              isPaymentTypesReady &&
              !getPaymentTypeByCode(String(value), paymentTypes)
            ) {
              return 'Selecione um meio de pagamento válido';
            }
            return undefined;
          },
        }}
        listeners={{
          onChange: ({ value, fieldApi }) => {
            if (isViewMode) return;
            const requiresBank = requiresBankAccountForPayment(
              String(value || ''),
              paymentTypes
            );
            if (!requiresBank) {
              fieldApi.form.setFieldValue('agency', '');
              fieldApi.form.setFieldValue('agencyDigit', '');
              fieldApi.form.setFieldValue('account', '');
              fieldApi.form.setFieldValue('accountDigit', '');
              return;
            }
            fieldApi.form.setFieldValue('agencyDigit', '');
            fieldApi.form.setFieldValue('accountDigit', '');
          },
        }}
        children={field => (
          <field.SelectField
            label='Meio de Pagamento'
            placeholder={
              isLoadingPaymentTypes
                ? 'Carregando meios de pagamento...'
                : 'Selecione o meio de pagamento'
            }
            required
            options={paymentMethodOptions}
            disabled={isDisabled}
          />
        )}
      />
      <form.AppField
        name='bestDueDay'
        validators={{
          onChange: ({ value, fieldApi }) => {
            if (isViewMode) return undefined;
            if (!shouldShowStep2FieldErrors(fieldApi)) return undefined;
            const raw = String(value || '').trim();
            if (!raw) return 'Informe o melhor dia de vencimento';
            if (!isValidBillingDay(raw)) {
              return 'Informe um dia entre 1 e 31';
            }
            return undefined;
          },
        }}
        children={field => (
          <field.TextField
            label='Melhor dia de vencimento'
            placeholder='Digite o dia do mês'
            required
            maxLength={2}
            disabled={isDisabled}
          />
        )}
      />
      <form.Subscribe
        selector={state => state.values}
        children={values => {
          const requiresBankAccount = requiresBankAccountForPayment(
            String(values.paymentMethod || ''),
            paymentTypes
          );
          if (!requiresBankAccount) return null;

          return (
            <>
              {renderBankField('agency', values, paymentTypes, bankFieldOptions)}
              {renderBankField(
                'agencyDigit',
                values,
                paymentTypes,
                bankFieldOptions
              )}
              {renderBankField('account', values, paymentTypes, bankFieldOptions)}
              {renderBankField(
                'accountDigit',
                values,
                paymentTypes,
                bankFieldOptions
              )}
            </>
          );
        }}
      />
    </div>
  );
}
